import { useEffect } from 'react'
import { selectPersonalContactSubscriptionData } from '../../SubscriptionForm/SubscriptionFormSelectors'
import { useDispatch, useSelector } from 'react-redux'
import { SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { mapSingleAddressData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Address/mapSingleAddress'
import { mapContactSubscriptionData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Contact/mapContactSubscriptionData'
import {
  importContractFromZohoData,
  updateConfig,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { useSnackbarNotification } from '@/shared/tools/notifications/useSnackbarNotification'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { selectAuthData } from '@/containers/Login/AuthSelectors'

const useZohoEventListener = () => {
  const { zohoId } = useSelector(selectPersonalContactSubscriptionData)
  const { country } = useSelector(selectAuthData)
  const dispatch = useDispatch()
  const showNotification = useSnackbarNotification()
  const { t } = useTranslation()

  useEffect(() => {
    if (!zohoId) {
      return
    }
    const channelName = `contacts.imported-from-zoho.${zohoId}`
    const channel = window.Echo.channel(channelName)

    channel.listen('.operation.success', event => {
      const addresses = event.data.addresses?.length > 0 ? event.data.addresses.map(address => mapSingleAddressData(address)) : []

      if (!event.data.contact) {
        dispatch(importContractFromZohoData({ contact: {}, addresses }))
        showNotification({
          type: SNACKBAR_TYPES.SUCCESS,
          message: t('common:notifications.address_insee_resolved'),
        })
        return
      }

      dispatch(
        importContractFromZohoData({ contact: mapContactSubscriptionData(event.data.contact, country), addresses }),
      )
      showNotification({
        type: SNACKBAR_TYPES.SUCCESS,
        message: t('common:notifications.contact_imported_successfully'),
      })
      dispatch(updateConfig({ isImportingContactFromZoho: false }))
    })

    channel.listen('.operation.failed', event => {
      if (import.meta.env.PROD) {
        Sentry.captureException(new Error('Broadcast error importing contact from Zoho'), { extra: { context: 'ZohoImportEcho', eventData: event } })
      }
      showNotification({
        type: SNACKBAR_TYPES.ERROR,
        message: t('common:notifications.cant_import_contact_from_zoho'),
      })
      dispatch(updateConfig({ isImportingContactFromZoho: false }))
    })

    return () => {
      channel.stopListening('.operation.success')
      channel.stopListening('.operation.failed')
      window.Echo.leaveChannel(channelName)
    }
  }, [zohoId, country])
}

export default useZohoEventListener
